import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FinanceSlider } from '@/components/finance/FinanceSlider';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { pricingMethods, latestMetricValue } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/project-pricing — the Project Pricing tool (F-013 S6.3). "What do I quote?" via
 * target-margin, markup, and rate-based methods. A calculator over your inputs; the default target
 * margin is seeded from your real gross margin. Real + flag-gated.
 */
export const Route = createFileRoute('/finance/forecast_/project-pricing')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <ProjectPricing />
    </FinanceGuard>
  ),
});


function ProjectPricing() {
  const { model } = useFinancialModel();
  const defaultMargin = Math.round(latestMetricValue(model?.metricsById['gross-margin']) ?? 30);

  const [jobCost, setJobCost] = useState(50_000);
  // Follows the model until touched (F-124 review #6, second sweep).
  const [targetMarginOv, setTargetMarginOv] = useState<number | null>(null);
  const targetMargin = targetMarginOv ?? defaultMargin;
  const setTargetMargin = setTargetMarginOv;
  const [hours, setHours] = useState(300);
  const [hourlyRate, setHourlyRate] = useState(150);

  const r = pricingMethods({ jobCost, targetMarginPct: targetMargin, hours, hourlyRate });

  const Method = ({ label, price, note }: { label: string; price: number; note: string }) => (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <p className="mt-1 ds-kpi-md text-foreground">{fmtCurrency(price)}</p>
      <p className="mt-1 text-[11.5px] text-muted-foreground">{note}</p>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <h1 className="ds-h1 mt-1">Project Pricing</h1>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">What to quote to hit your margin — three ways to price the same job.</p>

      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">This job</p>
          <div className="flex flex-col gap-2.5">
            <FinanceSlider label="Estimated job cost" value={jobCost} set={setJobCost} min={5_000} max={500_000} step={5_000} fmt={fmtCurrency} />
            <FinanceSlider label="Target gross margin" value={targetMargin} set={setTargetMargin} min={5} max={50} step={1} fmt={(v) => `${v}%`} />
            <FinanceSlider label="Estimated hours" value={hours} set={setHours} min={20} max={2_000} step={10} fmt={(v) => `${v} h`} />
            <FinanceSlider label="Charge-out rate ($/hr)" value={hourlyRate} set={setHourlyRate} min={60} max={400} step={5} fmt={(v) => `$${v}`} />
            {(() => {
              const maxP = Math.max(r.marginPrice, r.ratePrice) || 1;
              return (
                <div className="mt-1 border-t border-border pt-2.5">
                  <p className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">The two quotes, side by side</p>
                  <div className="mb-0.5 flex justify-between text-[11px] text-muted-foreground"><span>Margin method</span><span className="font-mono">{fmtCurrency(r.marginPrice)}</span></div>
                  <div className="h-3 overflow-hidden rounded bg-muted"><div className="h-3 rounded bg-[hsl(var(--primary))]" style={{ width: `${(r.marginPrice / maxP) * 100}%` }} /></div>
                  <div className="mb-0.5 mt-1.5 flex justify-between text-[11px] text-muted-foreground"><span>Rate method</span><span className="font-mono">{fmtCurrency(r.ratePrice)}</span></div>
                  <div className="h-3 overflow-hidden rounded bg-muted"><div className="h-3 rounded bg-[hsl(var(--info))]" style={{ width: `${(r.ratePrice / maxP) * 100}%` }} /></div>
                </div>
              );
            })()}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-1">
          <Method label="Target-margin price" price={r.marginPrice} note={`Prices in a ${targetMargin}% gross margin (cost ÷ (1 − margin)).`} />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <Method label="Same as a markup" price={r.marginPrice} note={`≈ ${r.markupPct.toFixed(0)}% markup on cost — the same price, said differently.`} />
            <Method label="Rate-based price" price={r.ratePrice} note={`${hours} h × $${hourlyRate}/hr → an implied ${r.rateImpliedMargin.toFixed(0)}% margin on this cost.`} />
          </div>
        </div>
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">A calculator over your inputs — the default margin comes from your real gross margin. Charge-out rate should already cover overhead recovery (see the Forecast’s break-even).</p>
      <ForecastToolNav current="project-pricing" />
    </div>
  );
}
